import React, { useState, useEffect } from "react";
import NoRecord from "../../Components/NoRecord";
import ContentLoader from "../../Components/ContentLoader";
import Paging from "../../Components/Paging";
import DatePicker from "../../ui/forms/DatePicker";
import useAuthStore from "../../store/authStore";
import useGeneralStore from "../../store/generalStore";
import IncomingCall from "../Components/IncomingCall";
import { getAgentMyCalls } from "../../API/agentApis";
import { downloadExotelCallRecordingUrl } from "../../API";
import { format, parseISO, isValid } from "date-fns";
import { BsPlayFill, BsPauseFill } from "react-icons/bs";

const LIMIT_PER_PAGE = 10;

export default function MyCalls() {
  const userData = useAuthStore((state) => state.userData);
  const showAlert = useGeneralStore((state) => state.open_alert);

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Summary statistics
  const [totalDurationSec, setTotalDurationSec] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);

  // Filter States
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [filterVerdict, setFilterVerdict] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Audio player state
  const [playingCallId, setPlayingCallId] = useState(null);
  const [audio] = useState(() => new Audio());

  useEffect(() => {
    const handleEnded = () => setPlayingCallId(null);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
    };
  }, [audio]);

  const handlePlayPause = (callId, recordingUrl) => {
    if (!recordingUrl) return;
    if (playingCallId === callId) {
      audio.pause();
      setPlayingCallId(null);
    } else {
      audio.src = downloadExotelCallRecordingUrl(recordingUrl);
      audio.play().catch((err) => {
        console.error("Audio playback error:", err);
        showAlert({ variant: "danger", msg: "Playback failed. Invalid audio URL." });
      });
      setPlayingCallId(callId);
    }
  };

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filterSearch);
      setCurrentPage(1);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [filterSearch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        size: LIMIT_PER_PAGE,
      };

      if (filterFromDate && isValid(filterFromDate)) {
        params.from_date = format(filterFromDate, "yyyy-MM-dd");
      }
      if (filterToDate && isValid(filterToDate)) {
        params.to_date = format(filterToDate, "yyyy-MM-dd");
      }
      if (filterVerdict) {
        params.verdict = filterVerdict;
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const res = await getAgentMyCalls(params);
      if (res?.status && res?.data) {
        setData(res.data.results || []);
        setCount(res.data.count || 0);
        setTotalDurationSec(res.data.total_duration_sec || 0);
        setTotalEarnings(res.data.total_earnings || 0);
      } else {
        setData([]);
        setCount(0);
        setTotalDurationSec(0);
        setTotalEarnings(0);
        showAlert({ variant: "danger", msg: "Failed to fetch calls log." });
      }
    } catch (error) {
      console.error(error);
      showAlert({ variant: "danger", msg: "Failed to fetch calls log." });
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch on page/filter change
  useEffect(() => {
    fetchData();
  }, [currentPage, filterFromDate, filterToDate, filterVerdict, debouncedSearch]);

  const handlePagination = (type, target) => {
    if (type === "directValue") {
      setCurrentPage(parseInt(target));
    }
  };

  const formatCallDate = (dateStr) => {
    if (!dateStr) return "—";
    const dateObj = parseISO(dateStr);
    return isValid(dateObj) ? format(dateObj, "dd/MMM/yyyy, hh:mm a") : dateStr;
  };

  const formatTalktime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getAuditBadge = (status) => {
    if (!status) return <span className="badge bg-secondary">Pending</span>;
    const lower = status.toLowerCase();
    if (lower.includes("good")) {
      return <span className="badge bg-success" style={{ fontSize: "0.8rem" }}>Good Call</span>;
    }
    if (lower.includes("bad")) {
      return <span className="badge bg-danger" style={{ fontSize: "0.8rem" }}>Bad Call</span>;
    }
    if (lower.includes("blank")) {
      return <span className="badge bg-warning text-dark" style={{ fontSize: "0.8rem" }}>Blank Call</span>;
    }
    return <span className="badge bg-info text-white" style={{ fontSize: "0.8rem" }}>{status}</span>;
  };

  const pageTotal = Math.ceil(count / LIMIT_PER_PAGE) || 1;

  return (
    <>
      <div className="pageContent imp-lead-layout">
        {/* Filter Controls Row */}
        <div className="card border-0 shadow-sm p-4 mb-4 rounded-3" style={{ backgroundColor: "#ffffff" }}>
          <div className="row g-3 align-items-end">
            {/* 1. From Date Filter */}
            <div className="col-md-2">
              <label className="form-label text-muted small fw-semibold mb-1">From Date</label>
              <DatePicker
                label="Select From"
                value={filterFromDate}
                onChange={(val) => {
                  setFilterFromDate(val);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* 2. To Date Filter */}
            <div className="col-md-2">
              <label className="form-label text-muted small fw-semibold mb-1">To Date</label>
              <DatePicker
                label="Select To"
                value={filterToDate}
                onChange={(val) => {
                  setFilterToDate(val);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* 3. Audio Verdict Filter */}
            <div className="col-md-2">
              <label className="form-label text-muted small fw-semibold mb-1">Audio Verdict</label>
              <select
                className="form-select"
                value={filterVerdict}
                onChange={(e) => {
                  setFilterVerdict(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ height: "38px" }}
              >
                <option value="">All Verdicts</option>
                <option value="Good Call">Good Call</option>
                <option value="Bad Call">Bad Call</option>
                <option value="Blank Call">Blank Call</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* 4. Search Filter */}
            <div className="col-md-4">
              <label className="form-label text-muted small fw-semibold mb-1">Search Borrower</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search Name, Number or LAN..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                style={{ height: "38px" }}
              />
            </div>

            {/* Reset Button */}
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFilterFromDate(null);
                  setFilterToDate(null);
                  setFilterVerdict("");
                  setFilterSearch("");
                  setDebouncedSearch("");
                  setCurrentPage(1);
                }}
                style={{ height: "38px" }}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Summary Statistics Cards Row */}
        <div className="row g-3 mb-4">
          {/* Card 1: Total Calls */}
          <div className="col-md-4">
            <div className="card border-0 shadow-sm p-3 rounded-3 d-flex flex-row align-items-center justify-content-between" style={{ backgroundColor: "#ffffff" }}>
              <div>
                <span className="text-muted small fw-semibold d-block mb-1">Total Call Count</span>
                <h3 className="fw-bold text-dark mb-0">{count}</h3>
              </div>
              <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#EBF5FB", color: "#2980B9" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Card 2: Total Talktime */}
          <div className="col-md-4">
            <div className="card border-0 shadow-sm p-3 rounded-3 d-flex flex-row align-items-center justify-content-between" style={{ backgroundColor: "#ffffff" }}>
              <div>
                <span className="text-muted small fw-semibold d-block mb-1">Total Talktime</span>
                <h3 className="fw-bold text-dark mb-0">{formatTalktime(totalDurationSec)}</h3>
              </div>
              <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#FEF9E7", color: "#F39C12" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
            </div>
          </div>

          {/* Card 3: Total Earning */}
          <div className="col-md-4">
            <div className="card border-0 shadow-sm p-3 rounded-3 d-flex flex-row align-items-center justify-content-between" style={{ backgroundColor: "#ffffff" }}>
              <div>
                <span className="text-muted small fw-semibold d-block mb-1">Total Earning</span>
                <h3 className="fw-bold text-success mb-0">₹{totalEarnings}</h3>
              </div>
              <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#E8F8F5", color: "#117A65" }}>
                <strong style={{ fontSize: "1.4rem" }}>₹</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="imp-leads-content mt-0">
          <div className="table-scroll" style={{ height: "100%", minHeight: "30rem", backgroundColor: "#ffffff", borderRadius: "8px", padding: "1rem" }}>
            {loading ? (
              <ContentLoader />
            ) : data.length === 0 ? (
              <NoRecord />
            ) : (
              <>
                <table className="agent-filter-table table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "15%" }}>Call Date & Time</th>
                      <th style={{ width: "30%" }}>Borrower Details</th>
                      <th style={{ width: "15%" }}>Disposition</th>
                      <th style={{ width: "15%", textAlign: "center" }}>Audit Verdict</th>
                      <th style={{ width: "15%", textAlign: "center" }}>Duration / Player</th>
                      <th style={{ width: "10%", textAlign: "right" }}>Earning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="fw-semibold text-dark">
                            {formatCallDate(row.start_time)}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <span className="fw-bold text-primary">{row.customer_name || "—"}</span>
                            <div className="d-flex gap-2 mt-1">
                              <span className="text-muted small bg-light px-2 py-0.5 rounded">
                                POS: ₹{row.pos !== undefined ? row.pos : "—"}
                              </span>
                              <span className="text-muted small bg-light px-2 py-0.5 rounded">
                                Client: {row.client_name || "—"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark text-wrap fw-semibold border" style={{ fontSize: "0.85rem", padding: "0.4rem 0.6rem" }}>
                            {row.disposition || "—"}
                          </span>
                        </td>
                        <td className="text-center">
                          {getAuditBadge(row.audit_status)}
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-column align-items-center gap-1">
                            <span className="text-muted small">
                              {Math.floor(row.duration_sec / 60)}m {row.duration_sec % 60}s
                            </span>
                            {row.recording_url ? (
                              <button
                                className={`btn btn-sm btn-icon ${
                                  playingCallId === row.id ? "btn-danger" : "btn-outline-primary"
                                } d-flex align-items-center justify-content-center gap-1 py-1 px-2`}
                                onClick={() => handlePlayPause(row.id, row.recording_url)}
                                style={{ borderRadius: "20px", fontSize: "0.75rem" }}
                              >
                                {playingCallId === row.id ? (
                                  <>
                                    <BsPauseFill size={14} /> Pause
                                  </>
                                ) : (
                                  <>
                                    <BsPlayFill size={14} /> Listen
                                  </>
                                )}
                              </button>
                            ) : (
                              <span className="text-muted small">No Audio</span>
                            )}
                          </div>
                        </td>
                        <td className="text-end fw-bold text-success fs-5">
                          ₹{row.earnings}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {pageTotal > 1 && (
                  <div className="pb-3 mt-4">
                    <Paging
                      currentPage={currentPage}
                      pageTotal={pageTotal}
                      handlePagination={handlePagination}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <IncomingCall />
    </>
  );
}
